import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Table, TableProps } from 'tea-component';
import { SortBy } from 'tea-component/table/addons';
import { useRequest } from 'ahooks';
import { fetchStudioOverallPeopleListData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { getTableList, getFirstRowTdV, getColumns } from './utils';
import { FIRST_ROW_OPT, TABLE_SUB_KEY } from './constants';
import TriggerFoldBtn from '@gp/components/TriggerFoldBtn';
import classNames from 'classnames';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';

const { sortable, autotip, injectable, mergeable, scrollable } = Table.addons;

interface BreakdownListPropsType {
  filterBOLoading: boolean;
  selectBO: string[];
}
type IRecords = {
  uniqueId: string;
  boName: string;
  isSpacerBlock: boolean;
  isAllTotal: boolean;
  isTotal: boolean;
  studioId: string | undefined;
  studioName: string | undefined;
  studioCover: string | undefined;
  nearest_year_fte: string;
  nearest_month_fte: string;
  current_year_fte: string;
  nearest_year_staff_cost: string;
  current_year_budget_staff_cost: string;
  current_month_staff_cost: string;
  current_month_staff_rate: string;
  ytm_staff_cost: string;
  ytm_staff_cost_overhead: string;
  ytm_staff_cost_marketing: string;
  ytm_staff_cost_dev: string;
};

const DEFAULT_PROPS: BreakdownListPropsType = {
  filterBOLoading: true,
  selectBO: [],
};

function BreakdownList(props: BreakdownListPropsType) {
  const { filterBOLoading, selectBO } = props;

  const [records, setRecords] = useState<IRecords[]>([]);
  const [sorts, setSorts] = useState<SortBy<any>>({ by: '', order: undefined });
  // 展开的group列
  const [unFoldList, setUnFoldList] = useState<string[]>([]);
  // 表格表头时间展示
  const [allTotalDateList, setAllTotalDateList] = useState<any>(null);
  // 列表第一行Portfolio Total的数据
  const [firstAllTotalList, setFirstAllTotalList] = useState<any>(null);
  // const [scrollTop, setScrollTop] = useState<number>(0);
  // // 滚动到表格第一行刚好触顶的距离
  // const [scrollCriticalTop, setScrollCriticalTop] = useState<number>(0);
  const firstRowContainerRef = useRef<HTMLElement>(null);
  const breakdownContainerRef = useRef<HTMLElement>(null);

  const [triggerFoldExpand, setTriggerFoldExpand] = useState<boolean>(false);

  // 列表数据接口
  const {
    loading: listLoading,
    data,
    run,
  } = useRequest(fetchStudioOverallPeopleListData, { manual: true });

  const loading = useMemo(() => {
    return filterBOLoading || listLoading;
  }, [filterBOLoading, listLoading]);

  // const isUnFoldLastCol = useMemo(() => {
  //   return unFoldList?.includes('ytm_staff_cost');
  // }, [unFoldList]);

  // const totalWidth = useMemo(() => {
  //   return isUnFoldLastCol ? 2385 : 1815;
  // }, [isUnFoldLastCol]);

  // 组织数据
  useEffect(() => {
    if (listLoading) return;
    const latestDate = data?.latest_date,
      latestCostDate = data?.latest_cost_date,
      rateDate = data?.rate_date;
    if (latestDate || latestCostDate || rateDate) {
      const currentYear = latestDate?.substring(0, 4);
      const currentMonth = latestDate?.substring(5, 7);
      const latestYear = String(Number(currentYear) - 1 || '');
      const costCurrentYear = latestCostDate?.substring(0, 4);
      const costCurrentMonth = latestCostDate?.substring(5, 7);
      const costLatestYear = String(Number(costCurrentYear) - 1 || '');
      // bo全选时设置
      !selectBO?.length &&
        setAllTotalDateList({
          nearest_year_fte: latestDate ? latestYear : '-',
          nearest_month_fte: latestDate
            ? `${currentYear}-${currentMonth}`
            : '-',
          current_year_fte: currentYear || '-',
          nearest_year_staff_cost: latestCostDate ? costLatestYear : '-',
          current_year_budget_staff_cost: costCurrentYear || '-',
          current_month_staff_cost: latestCostDate
            ? `${costCurrentYear}-${costCurrentMonth}`
            : '-',
          current_month_staff_rate: rateDate ? rateDate?.substring(0, 7) : '-',
        });
    }

    const newRecords = getTableList(data?.metrics || null);
    if (!selectBO?.length) {
      setFirstAllTotalList(newRecords?.[0]);
    }
    setRecords(newRecords);
    // 表格横向滚动了时，第一行得滚动到相应位置
    // const tBody = document.querySelector(
    //   '.dbg-studio-overall-people-list-table .tea-table__body',
    // );
    // if (tBody && tBody?.scrollLeft) {
    //   firstRowToScrollLeft(tBody?.scrollLeft);
    // }
  }, [listLoading]);

  const firstLineList = useMemo(() => {
    return firstAllTotalList;
  }, [firstAllTotalList]);

  // 第一行固定的内容是否显示
  const isShowFirstRow = useMemo(() => {
    return !loading && firstLineList;
  }, [loading, firstLineList]);

  // 根据数据 计算合并单元格情况
  const [rowGroupNumList, setRowGroupNumList] = useState<Array<number>>([]);
  useEffect(() => {
    if (loading) return;
    if (Array.isArray(records) && records?.length) {
      let tempList = [];
      let preRowId = 0,
        rowNum = 0;
      for (let i = 0; i < records?.length; i++) {
        const t = records?.[i];
        if (t?.isTotal) {
          rowNum = i - preRowId;
          preRowId = i;
          tempList.push(rowNum);
        }
      }
      tempList.push(records?.length - preRowId);
      tempList.shift();
      setRowGroupNumList(tempList);
    }
  }, [loading, records]);

  let groupIndex = 0;
  const [isColShadow, setColShadow] = useState(false);

  const addons = [
    sortable({
      columns: [
        'nearest_year_fte',
        'nearest_month_fte',
        'current_year_fte',
        'nearest_year_staff_cost',
        'current_year_budget_staff_cost',
        'current_month_staff_cost',
        'current_month_staff_rate',
        'ytm_staff_cost',
        'ytm_staff_cost_overhead',
        'ytm_staff_cost_marketing',
        'ytm_staff_cost_dev',
      ],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setSorts(value?.[0]);
      },
    }),
    scrollable({
      // minWidth: totalWidth,
      // maxHeight: 500,
    }),
    injectable({
      cell: (props, context) => ({
        className: `${
          (context.record?.isTotal && context.columnIndex !== 0) ||
          (context.columnIndex == 0 && context.recordIndex == 0)
            ? 'table-highlight-cell'
            : ''
        }${
          TABLE_SUB_KEY.includes(context.column.key)
            ? triggerFoldExpand
              ? ' table-expand-col show'
              : ' table-expand-col hide'
            : ''
        }${
          // table fixed属性和动态计算合并单元格冲突，此处手动模拟
          isColShadow
            ? context.columnIndex == 1
              ? ' table-col--fixed table-col--fixed-left'
              : context.columnIndex == 0
              ? ' table-col--fixed'
              : ''
            : ''
        }`,
      }),
      row: (props, context) => ({
        className:
          context.record?.isTotal && context.recordIndex !== 0
            ? 'table-group-row'
            : '',
      }),
    }),
    mergeable({
      rowSpan: (columnIndex, recordIndex, context) => {
        if (columnIndex === 0) {
          if (context.record?.isTotal && groupIndex < rowGroupNumList.length) {
            groupIndex++;
            return rowGroupNumList[groupIndex - 1];
          }
          return 0;
        }
        return 1;
      },
    }),
    autotip({
      isLoading: loading,
      hideIcon: !loading, // loading状态显示图标，其他情况不显示
      emptyText: 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  // 第一行固定的内容同步滚动
  // const firstRowToScrollLeft = useCallback((scrollLeft) => {
  //   if (firstRowContainerRef?.current) {
  //     // 通知添加第二列粘性定位阴影效果
  //     if (scrollLeft > 0) {
  //       firstRowContainerRef.current?.classList?.add('is-scrolled');
  //     } else {
  //       firstRowContainerRef.current?.classList?.remove('is-scrolled');
  //     }
  //     firstRowContainerRef.current.scrollLeft = scrollLeft;
  //   }
  // }, []);

  // 表格滚动时
  const handleScroll = useCallback((e) => {
    // const scrollLeft = e?.target?.scrollLeft;
    // firstRowToScrollLeft(scrollLeft);
    // table fixed属性和动态计算合并单元格冲突，此处手动模拟
    const scrollLeft = e?.target?.scrollLeft;
    if (scrollLeft > 0) {
      setColShadow(true);
    } else {
      setColShadow(false);
    }
  }, []);

  // 最外层容器纵向滚动时
  // const handleOverallBodyScroll = useCallback((e) => {
  //   const scrollTop = e?.target?.scrollTop;
  //   setScrollTop(scrollTop);
  //   setScrollCriticalTop((breakdownContainerRef?.current?.offsetTop || 0) + 77);
  // }, []);

  // 请求页面整体数据
  useEffect(() => {
    run({
      management_team: selectBO || [],
      order: sorts?.order || '',
      order_metric: sorts?.by || '',
    });
  }, [selectBO, sorts?.by, sorts?.order]);

  // 为表格添加遮挡间隙边框的dom
  // useEffect(() => {
  //   if (Array.isArray(records) && records?.length) {
  //     const tableEl = document.querySelector('.dbg-studio-overall-people-list-table');
  //     if (tableEl) {
  //       let spacerBlockNum = 0, studioLine = 0;
  //       for (let i = 0; i < records?.length; i++) {
  //         const t = records?.[i];
  //         t?.isTotal || studioLine++;
  //         if (t?.isSpacerBlock) {
  //           const shadeTop = ((i + 1) * 53 + spacerBlockNum * 20 - studioLine + 65) + 'px';
  //           const leftShade = document.createElement('span');
  //           leftShade.className = 'dbg-table-border-shade';
  //           leftShade.style.left = '0px';
  //           leftShade.style.top = shadeTop;
  //           const rightShade = document.createElement('span');
  //           rightShade.className = 'dbg-table-border-shade';
  //           rightShade.style.right = '0px';
  //           rightShade.style.top = shadeTop;
  //           tableEl.appendChild(leftShade);
  //           tableEl.appendChild(rightShade);
  //           spacerBlockNum++;
  //         }
  //       }
  //     }
  //   }
  // }, [records]);

  // 把遮挡边框的dom删除
  // const deleteEmptyBorder = useCallback(() => {
  //   const shadeEls = document.querySelectorAll('.dbg-studio-overall-people-list-table .dbg-table-border-shade');
  //   for (let i = 0; i < shadeEls?.length; i++) {
  //     const shadeEl = shadeEls?.[i];
  //     if (shadeEl) {
  //       shadeEl?.remove();
  //     }
  //   }
  // }, []);

  // loading时把遮挡边框的dom删除
  // useEffect(() => {
  //   if (loading) {
  //     deleteEmptyBorder();
  //   }
  // }, [loading]);

  // 添加滚动监听
  useEffect(() => {
    // 表体滚动监听，滚动时第一行固定的内容也滚动
    const t = setInterval(() => {
      const tBody = document.querySelector(
        '.dbg-studio-overall-people-list-table .tea-table__body',
      );
      if (tBody) {
        tBody?.addEventListener('scroll', handleScroll);
        clearInterval(t);
      }
    }, 100);
    // // 最外层容器滚动添加监听，用于表格第一行粘性定位加容器高度
    // const tt = setInterval(() => {
    //   const overallBody = document.querySelector('.dbg-studio-overall-body');
    //   if (overallBody) {
    //     overallBody?.addEventListener('scroll', handleOverallBodyScroll);
    //     clearInterval(tt);
    //   }
    // }, 100);

    return () => {
      const tBody = document.querySelector(
        '.dbg-studio-overall-people-list-table .tea-table__body',
      );
      if (tBody) {
        tBody?.removeEventListener('scroll', handleScroll);
      }
      // const overallBody = document.querySelector('.dbg-studio-overall-body');
      // if (overallBody) {
      //   overallBody?.removeEventListener('scroll', handleOverallBodyScroll);
      // }
    };
  }, []);

  return (
    <CardSlot
      ref={breakdownContainerRef}
      title="Headcount and Cost Breakdown"
      className="dbg-studio-overall-people-list-container"
      rightDom={
        <div className="header-fold-btn">
          <TriggerFoldBtn
            contentText="Expand by platform"
            foldBubble="Expand by platform"
            onClick={() => setTriggerFoldExpand(!triggerFoldExpand)}
          />
        </div>
      }
    >
      <Table
        className={classNames({
          'dbg-common-table dbg-studio-overall-people-list-table font14 first-item-h dbg-table-sticky dbh-action-state-center-table':
            true,
          'tbody-bottom-border': loading || !records?.length,
        })}
        records={
          loading
            ? []
            : records?.length
            ? records
            : firstLineList
            ? [firstLineList]
            : []
        }
        recordKey="uniqueId"
        columns={
          getColumns(
            allTotalDateList,
            unFoldList,
            setUnFoldList,
            triggerFoldExpand,
          ) as TableProps['columns']
        }
        addons={addons}
      />
      {/* 第一行表格内容，为了适配粘性定位 */}
      {
        /* isShowFirstRow */
        // false && (
        //   <section
        //     className="dbg-studio-overall-people-list-first-row"
        //     // 触发粘性定位零界点时，需要给容器加高度，不然粘性定位不生效，高度不够还是会滚动走
        //     // 现在改成动态改变top值，这样不会遮挡表头内容
        //     style={{
        //       // height: scrollTop > scrollCriticalTop ? (scrollTop - scrollCriticalTop + 53) : '',
        //       top: scrollTop > scrollCriticalTop ? (scrollTop - scrollCriticalTop + 130) : 130,
        //     }}
        //   >
        //     <section ref={firstRowContainerRef} className="dbg-studio-overall-people-list-first-row-container">
        //       <ul
        //         className="dbg-studio-overall-people-list-first-row-body"
        //         style={{
        //           minWidth: totalWidth,
        //         }}
        //       >
        //         {FIRST_ROW_OPT?.map((t) => {
        //           const value = getFirstRowTdV(firstLineList, t?.keyName, t?.format);
        //           return !t?.isFoldCol || (t?.isFoldCol && isUnFoldLastCol) ? (
        //             <li
        //               key={t?.keyName}
        //               className={classNames({
        //                 'dbg-studio-overall-people-list-first-row-item': true,
        //                 'body-gray-blue-bg': t?.isFoldCol,
        //               })}
        //               style={{
        //                 justifyContent: t?.align === 'left' ? 'flex-start' : 'flex-end',
        //                 width: t?.width / totalWidth * 100 + '%',
        //                 minWidth: `${t?.width}px`,
        //               }}
        //             >
        //               <span title={value}>
        //                 {value}
        //               </span>
        //             </li>
        //           ) : null;
        //         })}
        //         {/* <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-start',
        //             width: 150 / totalWidth * 100 + '%',
        //             minWidth: '150px',
        //           }}
        //         >
        //           <span title={firstLineList?.boName || '-'}>
        //             {firstLineList?.boName || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-start',
        //             width: 200 / totalWidth * 100 + '%',
        //             minWidth: '200px',
        //           }}
        //         >
        //           <span title={firstLineList?.studioName || '-'}>
        //             {firstLineList?.studioName || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={isValidNumber(firstLineList?.['nearest_year_fte']) ? String(Number(firstLineList?.['nearest_year_fte'])) : '-'}>
        //             {isValidNumber(firstLineList?.['nearest_year_fte']) ? Number(firstLineList?.['nearest_year_fte']) : '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={isValidNumber(firstLineList?.['nearest_month_fte']) ? String(Number(firstLineList?.['nearest_month_fte'])) : '-'}>
        //             {isValidNumber(firstLineList?.['nearest_month_fte']) ? Number(firstLineList?.['nearest_month_fte']) : '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={isValidNumber(firstLineList?.['current_year_fte']) ? String(Number(firstLineList?.['current_year_fte'])) : '-'}>
        //             {isValidNumber(firstLineList?.['current_year_fte']) ? Number(firstLineList?.['current_year_fte']) : '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={getFormatNumPlus(firstLineList?.['nearest_year_staff_cost']) || '-'}>
        //             {getFormatNumPlus(firstLineList?.['nearest_year_staff_cost']) || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 225 / totalWidth * 100 + '%',
        //             minWidth: '225px',
        //           }}
        //         >
        //           <span title={getFormatNumPlus(firstLineList?.['current_year_budget_staff_cost']) || '-'}>
        //             {getFormatNumPlus(firstLineList?.['current_year_budget_staff_cost']) || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={getFormatNumPlus(firstLineList?.['current_month_staff_cost']) || '-'}>
        //             {getFormatNumPlus(firstLineList?.['current_month_staff_cost']) || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 160 / totalWidth * 100 + '%',
        //             minWidth: '160px',
        //           }}
        //         >
        //           <span title={getFormatNumPlus(firstLineList?.['current_month_staff_rate']) || '-'}>
        //             {getFormatNumPlus(firstLineList?.['current_month_staff_rate']) || '-'}
        //           </span>
        //         </li>
        //         <li
        //           className="dbg-studio-overall-people-list-first-row-item"
        //           style={{
        //             justifyContent: 'flex-end',
        //             width: 205 / totalWidth * 100 + '%',
        //             minWidth: '205px',
        //           }}
        //         >
        //           <span title={getFormatNumPlus(firstLineList?.['ytm_staff_cost']) || '-'}>
        //             {getFormatNumPlus(firstLineList?.['ytm_staff_cost']) || '-'}
        //           </span>
        //         </li>
        //         {isUnFoldLastCol && (
        //           <>
        //             <li
        //               className="dbg-studio-overall-people-list-first-row-item body-gray-blue-bg"
        //               style={{
        //                 justifyContent: 'flex-end',
        //                 width: 190 / totalWidth * 100 + '%',
        //                 minWidth: '190px',
        //               }}
        //             >
        //               <span title={getFormatNumPlus(firstLineList?.['ytm_staff_cost_overhead']) || '-'}>
        //                 {getFormatNumPlus(firstLineList?.['ytm_staff_cost_overhead']) || '-'}
        //               </span>
        //             </li>
        //             <li
        //               className="dbg-studio-overall-people-list-first-row-item body-gray-blue-bg"
        //               style={{
        //                 justifyContent: 'flex-end',
        //                 width: 190 / totalWidth * 100 + '%',
        //                 minWidth: '190px',
        //               }}
        //             >
        //               <span title={getFormatNumPlus(firstLineList?.['ytm_staff_cost_marketing']) || '-'}>
        //                 {getFormatNumPlus(firstLineList?.['ytm_staff_cost_marketing']) || '-'}
        //               </span>
        //             </li>
        //             <li
        //               className="dbg-studio-overall-people-list-first-row-item body-gray-blue-bg"
        //               style={{
        //                 justifyContent: 'flex-end',
        //                 width: 190 / totalWidth * 100 + '%',
        //                 minWidth: '190px',
        //               }}
        //             >
        //               <span title={getFormatNumPlus(firstLineList?.['ytm_staff_cost_dev']) || '-'}>
        //                 {getFormatNumPlus(firstLineList?.['ytm_staff_cost_dev']) || '-'}
        //               </span>
        //             </li>
        //           </>
        //         )} */}
        //       </ul>
        //     </section>
        //   </section>
        // )
      }
    </CardSlot>
  );
}

BreakdownList.defaultProps = DEFAULT_PROPS;

export default memo(BreakdownList);
